import { useState, useEffect, useRef } from "react";
import {
  BellRing,
  CalendarClock,
  Sparkles,
  Check,
  X,
  Smartphone,
} from "lucide-react";

export default function CreatorPOVSection() {
  const [started, setStarted] = useState(false);
  const [decision, setDecision] = useState(null);
  const sectionRef = useRef(null);

  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setStarted(true);
      },
      { threshold: 0.2 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <section
      id="creator-pov"
      ref={sectionRef}
      data-testid="creator-pov-section"
      className="relative py-14 sm:py-[4.25rem] overflow-hidden border-t border-violet-500/[0.08]"
    >
      <div className="absolute top-1/3 right-0 w-[min(100vw,380px)] h-[320px] bg-fuchsia-600/[0.04] rounded-full blur-[100px] pointer-events-none" />

      <div className="max-w-7xl mx-auto px-6 sm:px-8 relative">
        <div className="reveal text-center mb-10 sm:mb-12 max-w-2xl mx-auto">
          <p className="text-xs tracking-[0.2em] uppercase font-semibold text-violet-300/55 mb-3">
            Your side of the link
          </p>
          <h2 className="text-3xl sm:text-4xl tracking-tight font-semibold mb-3">
            You made the link.{" "}
            <span className="bg-gradient-to-r from-violet-300 via-violet-400 to-fuchsia-400 bg-clip-text text-transparent">
              We keep you in the loop
            </span>
          </h2>
          <p className="text-sm sm:text-base text-violet-200/40 leading-relaxed">
            When a lead qualifies or requests time, you get in-app alerts and push
            notifications with an AI-written summary so you can accept, decline, or follow up
            without digging through threads.
          </p>
        </div>

        <div className="max-w-lg mx-auto">
          <div
            className={`rounded-[1.35rem] border border-violet-500/15 bg-gradient-to-b from-[#16131f] to-[#0e0c14] shadow-2xl shadow-violet-950/35 overflow-hidden transition-all duration-700 motion-reduce:opacity-100 motion-reduce:translate-y-0 ${
              started ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
            }`}
            data-testid="creator-pov-device"
          >
            {/* Status bar strip */}
            <div className="flex items-center justify-between px-4 pt-3 pb-2 border-b border-violet-500/10 bg-[#0a0810]">
              <div className="flex items-center gap-1.5 text-[10px] font-medium text-white/35">
                <Smartphone className="w-3 h-3" aria-hidden />
                ReachMeNow
              </div>
              <span className="text-[10px] text-white/25 tabular-nums">9:41</span>
            </div>

            <div className="p-4 sm:p-5 space-y-4">
              {/* Push-style banner */}
              <div
                className={`flex gap-3 rounded-xl border border-violet-500/20 bg-violet-500/[0.08] p-3 transition-all duration-500 delay-100 motion-reduce:opacity-100 motion-reduce:translate-y-0 ${
                  started ? "opacity-100 translate-y-0" : "opacity-0 translate-y-3"
                }`}
                data-testid="creator-pov-push"
              >
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-violet-600/30 text-violet-200">
                  <BellRing className="w-5 h-5" aria-hidden />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-[10px] font-semibold uppercase tracking-wider text-violet-300/70">
                    Push notification
                  </p>
                  <p className="text-sm font-semibold text-white mt-0.5">
                    New meeting request
                  </p>
                  <p className="text-xs text-white/45 mt-0.5">
                    Sarah · Discovery call · Tue 2:00 PM
                  </p>
                </div>
              </div>

              {/* In-app card */}
              <div
                className={`rounded-xl border border-violet-500/12 bg-[#12101c] p-4 space-y-3 transition-all duration-500 delay-200 motion-reduce:opacity-100 motion-reduce:translate-y-0 ${
                  started ? "opacity-100 translate-y-0" : "opacity-0 translate-y-3"
                }`}
                data-testid="creator-pov-inapp"
              >
                <div className="flex items-center gap-2 text-violet-300/80">
                  <CalendarClock className="w-4 h-4 shrink-0" aria-hidden />
                  <span className="text-xs font-semibold">In your ReachMeNow inbox</span>
                </div>

                <div className="flex items-start gap-2 rounded-lg bg-violet-500/[0.06] border border-violet-500/10 p-3">
                  <Sparkles className="w-4 h-4 text-fuchsia-300/90 shrink-0 mt-0.5" aria-hidden />
                  <div>
                    <p className="text-[11px] font-semibold uppercase tracking-wide text-fuchsia-200/70 mb-1.5">
                      AI summary
                    </p>
                    <ul className="text-xs text-white/65 space-y-1.5 leading-relaxed">
                      <li>Budget $3-5k, launch target ~6 weeks. Matches your discovery offer.</li>
                      <li>Asked about brand identity; timeline and scope look qualified.</li>
                      <li>Suggested slot: Tue 2:00 PM (20 min). Awaiting your confirmation.</li>
                    </ul>
                  </div>
                </div>

                {decision === null ? (
                  <div className="flex flex-col sm:flex-row gap-2 pt-1">
                    <button
                      type="button"
                      data-testid="creator-pov-decline"
                      onClick={() => setDecision("declined")}
                      className="flex-1 flex items-center justify-center gap-2 min-h-11 rounded-xl border border-white/12 bg-white/[0.04] text-sm font-medium text-white/70 hover:bg-white/[0.07] hover:text-white transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-400 focus-visible:ring-offset-2 focus-visible:ring-offset-[#12101c]"
                    >
                      <X className="w-4 h-4" aria-hidden />
                      Decline
                    </button>
                    <button
                      type="button"
                      data-testid="creator-pov-accept"
                      onClick={() => setDecision("accepted")}
                      className="flex-1 flex items-center justify-center gap-2 min-h-11 rounded-xl bg-gradient-to-r from-violet-500 to-fuchsia-600 text-sm font-medium text-white shadow-lg shadow-violet-600/20 hover:from-violet-400 hover:to-fuchsia-500 transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-300 focus-visible:ring-offset-2 focus-visible:ring-offset-[#12101c]"
                    >
                      <Check className="w-4 h-4" aria-hidden />
                      Accept
                    </button>
                  </div>
                ) : (
                  <div
                    className={`rounded-xl border p-3 flex items-start gap-2 animate-fade-in ${
                      decision === "accepted"
                        ? "border-emerald-500/30 bg-emerald-500/[0.1]"
                        : "border-white/10 bg-white/[0.04]"
                    }`}
                    data-testid="creator-pov-decision"
                    role="status"
                  >
                    {decision === "accepted" ? (
                      <>
                        <Check className="w-4 h-4 text-emerald-400 shrink-0 mt-0.5" aria-hidden />
                        <div>
                          <p className="text-sm font-semibold text-emerald-200">
                            Confirmed · added to your calendar
                          </p>
                          <p className="text-xs text-emerald-200/60 mt-0.5">
                            Sarah · Tue 2:00 PM · 20 min discovery
                          </p>
                        </div>
                      </>
                    ) : (
                      <>
                        <X className="w-4 h-4 text-white/45 shrink-0 mt-0.5" aria-hidden />
                        <div>
                          <p className="text-sm font-semibold text-white/80">Request declined</p>
                          <p className="text-xs text-white/45 mt-0.5">
                            We&apos;ll send a polite note. You can follow up manually anytime.
                          </p>
                        </div>
                      </>
                    )}
                  </div>
                )}

                {decision !== null && (
                  <button
                    type="button"
                    data-testid="creator-pov-reset"
                    onClick={() => setDecision(null)}
                    className="w-full text-[11px] font-medium text-violet-300/55 hover:text-violet-200/80 py-1 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-400 focus-visible:ring-offset-2 focus-visible:ring-offset-[#12101c] rounded-lg"
                  >
                    Reset demo
                  </button>
                )}
              </div>

              <p className="text-[10px] text-center text-white/25 px-2">
                Illustrative flow. Actual copy and fields match your workspace settings.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
